import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence, useAnimation } from 'framer-motion'
import { 
  SparklesIcon, 
  BoltIcon, 
  ChatBubbleLeftRightIcon,
  RocketLaunchIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

interface OnboardingStep {
  id: number
  title: string
  subtitle: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  gradient: string
  particles: number
  visual: 'sparkles' | 'lightning' | 'chat' | 'rocket'
}

const steps: OnboardingStep[] = [
  {
    id: 1,
    title: "Welcome to JPDZ",
    subtitle: "AI-Powered Tech Intelligence",
    description: "Experience the future of technology research with instant AI-powered insights across all devices and categories.",
    icon: SparklesIcon,
    gradient: "from-black via-orange-900 to-orange-600",
    particles: 50,
    visual: 'sparkles'
  },
  {
    id: 2,
    title: "Lightning Fast",
    subtitle: "Instant Research & Analysis",
    description: "Get comprehensive product analysis, comparisons, and recommendations in seconds, not hours.",
    icon: BoltIcon,
    gradient: "from-orange-600 via-orange-500 to-black",
    particles: 40,
    visual: 'lightning'
  },
  {
    id: 3,
    title: "Smart Conversations",
    subtitle: "Natural AI Interactions",
    description: "Chat naturally about any tech product and get expert-level insights tailored to your specific needs.",
    icon: ChatBubbleLeftRightIcon,
    gradient: "from-black via-orange-800 to-orange-500",
    particles: 35,
    visual: 'chat'
  },
  {
    id: 4,
    title: "Ready to Launch",
    subtitle: "Your Tech Journey Begins",
    description: "Join thousands of users making smarter tech decisions with AI-powered research and insights.",
    icon: RocketLaunchIcon,
    gradient: "from-orange-500 via-orange-600 to-black",
    particles: 60,
    visual: 'rocket'
  }
]

interface FullScreenOnboardingProps {
  onComplete: () => void
}

const FullScreenOnboarding: React.FC<FullScreenOnboardingProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const controls = useAnimation()

  const nextStep = async () => {
    if (isAnimating) return
    
    setIsAnimating(true)
    
    if (currentStep < steps.length - 1) {
      await controls.start({ scale: 0.95, opacity: 0.8 })
      setCurrentStep(currentStep + 1)
      await controls.start({ scale: 1, opacity: 1 })
    } else {
      await controls.start({ scale: 1.1, opacity: 0 })
      onComplete()
    }
    
    setIsAnimating(false)
  }

  const skipToEnd = () => {
    onComplete()
  }

  useEffect(() => {
    controls.start({ scale: 1, opacity: 1 })
  }, [currentStep, controls])

  const currentStepData = steps[currentStep]

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Animated Background */}
      <motion.div 
        className={`absolute inset-0 bg-gradient-to-br ${currentStepData.gradient}`}
        animate={{
          background: [
            `linear-gradient(135deg, ${currentStepData.gradient.split(' ')[1]}, ${currentStepData.gradient.split(' ')[3]})`,
            `linear-gradient(225deg, ${currentStepData.gradient.split(' ')[3]}, ${currentStepData.gradient.split(' ')[1]})`,
            `linear-gradient(135deg, ${currentStepData.gradient.split(' ')[1]}, ${currentStepData.gradient.split(' ')[3]})`
          ]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Particle System */}
      <ParticleSystem count={currentStepData.particles} />

      {/* Floating Geometric Shapes */}
      <FloatingShapes />

      {/* Main Content Container */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-8">
        <motion.div
          animate={controls}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex justify-center space-x-3 mb-6">
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    index <= currentStep 
                      ? 'bg-white w-12' 
                      : 'bg-white/30 w-8'
                  }`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                />
              ))}
            </div>
            <motion.p 
              className="text-white/80 text-sm font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {currentStep + 1} of {steps.length}
            </motion.p>
          </div>

          {/* Icon with Visual Effect */}
          <motion.div
            className="mb-8 relative"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <div className="relative inline-block">
              {/* Glowing Background */}
              <motion.div
                className="absolute inset-0 bg-white/20 rounded-full blur-xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              {/* Icon Container */}
              <motion.div
                className="relative bg-orange-500/20 backdrop-blur-xl rounded-full p-8 border border-orange-400/30"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <currentStepData.icon className="w-16 h-16 text-orange-400" />
              </motion.div>

              {/* Visual Effects */}
              <VisualEffect type={currentStepData.visual} />
            </div>
          </motion.div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="mb-12"
            >
              <motion.h1 
                className="text-5xl md:text-7xl font-bold text-white mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                {currentStepData.title}
              </motion.h1>
              
              <motion.h2 
                className="text-2xl md:text-3xl font-semibold text-white/90 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {currentStepData.subtitle}
              </motion.h2>
              
              <motion.p 
                className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                {currentStepData.description}
              </motion.p>
            </motion.div>
          </AnimatePresence>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <motion.button
              onClick={nextStep}
              disabled={isAnimating}
              className="group relative bg-gradient-to-r from-orange-500 to-orange-600 backdrop-blur-xl border border-orange-400/30 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 hover:from-orange-600 hover:to-orange-700 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/25"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="flex items-center gap-3">
                {currentStep === steps.length - 1 ? (
                  <>
                    <RocketLaunchIcon className="w-6 h-6" />
                    Get Started
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </span>
              
              {/* Button Glow Effect */}
              <motion.div
                className="absolute inset-0 bg-orange-500/30 rounded-2xl blur-xl"
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                  scale: [1, 1.05, 1]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.button>

            {currentStep < steps.length - 1 && (
              <motion.button
                onClick={skipToEnd}
                className="text-white/70 hover:text-white transition-colors duration-300 font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Skip to app
              </motion.button>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Skip Button (Top Right) */}
      {currentStep < steps.length - 1 && (
        <motion.button
          onClick={skipToEnd}
          className="absolute top-8 right-8 z-20 text-white/70 hover:text-white transition-colors duration-300 font-medium"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Skip
        </motion.button>
      )}
    </div>
  )
}

// Particle System Component
const ParticleSystem: React.FC<{ count: number }> = ({ count }) => {
  const particles = Array.from({ length: count }, (_, i) => i)

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle}
          className="absolute w-1 h-1 bg-white/30 rounded-full"
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            opacity: 0
          }}
          animate={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      ))}
    </div>
  )
}

// Floating Geometric Shapes
const FloatingShapes: React.FC = () => {
  const shapes = Array.from({ length: 8 }, (_, i) => i)

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map((shape) => (
        <motion.div
          key={shape}
          className={`absolute ${
            shape % 3 === 0 ? 'w-20 h-20 rounded-full' :
            shape % 3 === 1 ? 'w-16 h-16 rounded-lg rotate-45' :
            'w-24 h-6 rounded-full'
          } bg-white/5 backdrop-blur-sm border border-white/10`}
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            rotate: 0
          }}
          animate={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            rotate: 360
          }}
          transition={{
            duration: Math.random() * 20 + 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      ))}
    </div>
  )
}

// Visual Effects for Each Step
const VisualEffect: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'sparkles':
      return (
        <div className="absolute inset-0">
          {Array.from({ length: 12 }, (_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`
              }}
              animate={{
                scale: [0, 1, 0],
                rotate: [0, 180, 360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2
              }}
            >
              <SparklesIcon className="w-full h-full text-white" />
            </motion.div>
          ))}
        </div>
      )
    
    case 'lightning':
      return (
        <motion.div
          className="absolute -inset-4"
          animate={{
            background: [
              'radial-gradient(circle, rgba(255,255,0,0.3) 0%, transparent 70%)',
              'radial-gradient(circle, rgba(255,255,0,0.6) 0%, transparent 70%)',
              'radial-gradient(circle, rgba(255,255,0,0.3) 0%, transparent 70%)'
            ]
          }}
          transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
        />
      )
    
    case 'chat':
      return (
        <div className="absolute inset-0">
          {Array.from({ length: 6 }, (_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 bg-white/40 rounded-full"
              style={{
                left: `${30 + (i * 10)}%`,
                top: `${40 + Math.sin(i) * 20}%`
              }}
              animate={{
                y: [-10, 10, -10],
                opacity: [0.4, 1, 0.4]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3
              }}
            />
          ))}
        </div>
      )
    
    case 'rocket':
      return (
        <motion.div
          className="absolute -bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{
            y: [-5, 5, -5],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-16 bg-gradient-to-t from-orange-400 to-yellow-300 rounded-full opacity-60 blur-sm" />
        </motion.div>
      )
    
    default:
      return null
  }
}

export default FullScreenOnboarding